import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Trash2, Plus, Settings, Eye, EyeOff } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const STORAGE_KEY = "flexpay.apiTester.env";

/**
 * Read the saved environment from localStorage.
 * Shape: [{ key: "partner_id", value: "abc-123", secret: false }, ...]
 */
export function loadEnv() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export function saveEnv(rows) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(rows || []));
  } catch {
    /* quota? — silent */
  }
}

/**
 * Substitute {{varname}} tokens in any string with the value from `env`.
 * Returns the original input unchanged if no tokens or no env.
 */
export function substituteVars(input, env) {
  if (typeof input !== "string" || !input.includes("{{")) return input;
  const map = Object.fromEntries((env || []).map((r) => [r.key, r.value]));
  return input.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (_, k) => (k in map ? map[k] : `{{${k}}}`));
}

/**
 * Recursively substitute vars inside an object/array.
 */
export function substituteDeep(obj, env) {
  if (typeof obj === "string") return substituteVars(obj, env);
  if (Array.isArray(obj)) return obj.map((x) => substituteDeep(x, env));
  if (obj && typeof obj === "object") {
    const out = {};
    Object.entries(obj).forEach(([k, v]) => { out[k] = substituteDeep(v, env); });
    return out;
  }
  return obj;
}

/**
 * Manage environment dialog component.
 * Props: open, onOpenChange, onChange (called with new env array after save)
 */
export default function EnvManager({ open, onOpenChange, onChange }) {
  const [rows, setRows] = useState([]);
  const [reveal, setReveal] = useState({});

  useEffect(() => {
    if (open) {
      const data = loadEnv();
      setRows(data.length ? data : [{ key: "", value: "", secret: false }]);
      setReveal({});
    }
  }, [open]);

  const update = (i, patch) => setRows((rs) => rs.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const remove = (i) => setRows((rs) => rs.filter((_, idx) => idx !== i));
  const addRow = () => setRows((rs) => [...rs, { key: "", value: "", secret: false }]);

  const save = () => {
    // Filter out empty rows; validate keys are unique + valid
    const clean = rows
      .map((r) => ({ key: (r.key || "").trim(), value: r.value ?? "", secret: !!r.secret }))
      .filter((r) => r.key);
    const keys = clean.map((r) => r.key);
    const dupes = keys.filter((k, i) => keys.indexOf(k) !== i);
    if (dupes.length) { toast.error(`Duplicate keys: ${[...new Set(dupes)].join(", ")}`); return; }
    const invalid = keys.filter((k) => !/^[\w.]+$/.test(k));
    if (invalid.length) { toast.error(`Invalid keys (use letters/digits/_/.): ${invalid.join(", ")}`); return; }
    saveEnv(clean);
    onChange?.(clean);
    onOpenChange?.(false);
    toast.success(`Environment saved · ${clean.length} variable(s)`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="env-manager-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display">
            <Settings className="h-5 w-5" /> Environment variables
          </DialogTitle>
          <DialogDescription>
            Stored in your browser (no backend round-trip). Reference anywhere with <code className="text-xs bg-muted px-1 py-0.5 rounded">{`{{varname}}`}</code> — keys can contain letters, digits, underscores, dots.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-1">
          <div className="grid grid-cols-[1fr_2fr_auto_auto] gap-2 text-[10px] font-bold tracking-wider uppercase text-muted-foreground pb-1 border-b">
            <span>Key</span>
            <span>Value</span>
            <span>Secret</span>
            <span></span>
          </div>
          {rows.map((row, i) => (
            <div key={i} className="grid grid-cols-[1fr_2fr_auto_auto] gap-2 items-center">
              <Input
                value={row.key}
                onChange={(e) => update(i, { key: e.target.value })}
                placeholder="partner_token"
                className="font-mono text-sm h-9"
                data-testid={`env-key-${i}`}
              />
              <Input
                type={row.secret && !reveal[i] ? "password" : "text"}
                value={row.value}
                onChange={(e) => update(i, { value: e.target.value })}
                placeholder="value"
                className="font-mono text-sm h-9"
                data-testid={`env-value-${i}`}
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => update(i, { secret: !row.secret })}
                className="h-9 w-9 p-0"
                title={row.secret ? "Hide value (currently secret)" : "Mark as secret"}
              >
                {row.secret ? (
                  reveal[i] ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />
                ) : (
                  <Eye className="h-3.5 w-3.5 opacity-30" />
                )}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => remove(i)}
                className="h-9 w-9 p-0 text-destructive"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
          <Button type="button" variant="ghost" size="sm" onClick={addRow} className="mt-2 text-xs">
            <Plus className="h-3.5 w-3.5 mr-1" /> Add variable
          </Button>
        </div>

        <div className="text-[11px] text-muted-foreground bg-muted/50 rounded-lg p-3 mt-2">
          <p className="font-semibold mb-1">Example</p>
          <code className="text-[10px]">key: <strong>partner_token</strong> · value: <strong>eyJhbGc...</strong></code>
          <br />
          <span>In any param: <code className="text-[10px]">{`Bearer {{partner_token}}`}</code> → substituted on Run.</span>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange?.(false)}>Cancel</Button>
          <Button onClick={save} data-testid="env-save">Save environment</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
